/**
 * Timber Sizes Sheet
 * ------------------
 * Mobile-first bottom-sheet (and desktop centered modal) that shows a
 * privacy-safe summary of timber sizes for a single structure on the public
 * proposal page. Renders only when an opt-in employee toggle is on.
 *
 * The sheet itself does not decide what's safe to show — it consumes the
 * pre-deduped output of `summarizeTimbersForClient`, which is the single
 * source of truth for "what's exposed to the client".
 *
 * UX notes:
 *  - Bottom-sheet on `<= 720px` viewports — slides up from the bottom and
 *    can be dragged down to dismiss (touch only).
 *  - Centered modal on wider viewports.
 *  - Backdrop click and Escape both close.
 *  - Body scrolls independently when the list is long; the sticky header
 *    keeps the structure name and close button visible.
 */

import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import type { ClientTimberRow } from '../../utils/clientTimberSummary';

export interface TimberSizesSheetProps {
  open: boolean;
  onClose: () => void;
  /** Display name of the structure these rows belong to. */
  structureName: string;
  /** Pre-summarized rows. Empty arrays still render an empty-state row. */
  rows: ClientTimberRow[];
}

const TimberSizesSheet: React.FC<TimberSizesSheetProps> = ({ open, onClose, structureName, rows }) => {
  // Drag-down-to-dismiss state. Tracked in pixels of vertical offset; only
  // applied as inline transform during the drag so the CSS animation stays
  // in charge of open/close transitions.
  const [dragY, setDragY] = useState(0);
  const dragStartYRef = useRef<number | null>(null);
  const sheetRef = useRef<HTMLDivElement | null>(null);

  // Lock body scroll while open — avoids the awkward double-scroll where the
  // background page scrolls behind the sheet on mobile.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // ESC closes.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Reset any leftover drag offset when the sheet reopens.
  useEffect(() => {
    if (open) setDragY(0);
  }, [open]);

  if (!open) return null;

  const onTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length !== 1) return;
    dragStartYRef.current = e.touches[0].clientY;
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (dragStartYRef.current == null || e.touches.length !== 1) return;
    const dy = e.touches[0].clientY - dragStartYRef.current;
    // Only allow downward drag — translating upward feels broken on mobile
    // because there's nothing above the sheet to expose.
    if (dy > 0) setDragY(dy);
  };
  const onTouchEnd = () => {
    if (dragStartYRef.current == null) return;
    dragStartYRef.current = null;
    // Dismiss past a 80px threshold — generous enough that an accidental
    // 20-30px drag during scroll doesn't close the sheet.
    if (dragY > 80) {
      onClose();
    }
    setDragY(0);
  };

  const dragStyle = dragY > 0
    ? { transform: `translateY(${dragY}px)`, transition: 'none' }
    : undefined;

  return (
    <div
      className="timber-sheet-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={`Timber sizes for ${structureName}`}
      onClick={e => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="timber-sheet"
        ref={sheetRef}
        style={dragStyle}
        onClick={e => e.stopPropagation()}
      >
        {/* Drag handle — both a visual affordance and the only touch surface
            that listens for drag-down. Listening on the whole sheet would
            steal touchmove from the scrollable list. */}
        <div
          className="timber-sheet-handle-wrap"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          aria-hidden
        >
          <span className="timber-sheet-handle" />
        </div>
        <header className="timber-sheet-header">
          <div className="timber-sheet-header-text">
            <p className="timber-sheet-eyebrow">Timber sizes</p>
            <h3 className="timber-sheet-title">{structureName}</h3>
          </div>
          <button
            type="button"
            className="timber-sheet-close"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </header>
        <div className="timber-sheet-body">
          {rows.length === 0 ? (
            <p className="timber-sheet-empty">No timber sizes are listed for this structure yet.</p>
          ) : (
            <ul className="timber-sheet-list" role="list">
              {rows.map(row => (
                <li key={row.key} className="timber-sheet-row">
                  <span className="timber-sheet-row-name">{row.name}</span>
                  <span className="timber-sheet-row-size" aria-label="cross-section">{row.crossSection}</span>
                  <span className="timber-sheet-row-grade">{row.grade}</span>
                  <span className="timber-sheet-row-dryness">{row.dryness}</span>
                </li>
              ))}
            </ul>
          )}
          <p className="timber-sheet-footnote">
            Cross-section dimensions only — lengths and quantities aren't shown.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TimberSizesSheet;
